import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { toast } from "sonner";
import { Mail, Lock, User, Globe } from "lucide-react";
import { AuthShell, Field } from "./Login";
import { api } from "../lib/api";

export default function Signup() {
  const { signup, setUser } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [country, setCountry] = useState("United States");
  const [currency, setCurrency] = useState("USD");
  const [countries, setCountries] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meta/countries")
      .then((r) => setCountries(r.data?.countries || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const m = countries.find((c) => c.name === country);
    if (m) setCurrency(m.currency);
  }, [country, countries]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Bypass AuthContext.signup to send country + currency
      const res = await api.post("/auth/signup", { name, email, password, country, currency });
      if (res.data.token) localStorage.setItem("cavilo_token", res.data.token);
      setUser(res.data.user);
      toast.success("Welcome to Cavilow!");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Create your account" subtitle="Free forever. Upgrade only when you need more.">
      <form onSubmit={submit} className="space-y-4">
        <Field icon={User} label="Full name">
          <input
            data-testid="signup-name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            placeholder="Alex Morgan"
          />
        </Field>
        <Field icon={Mail} label="Email">
          <input
            data-testid="signup-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            placeholder="you@work.com"
          />
        </Field>
        <Field icon={Lock} label="Password">
          <input
            data-testid="signup-password"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-transparent outline-none text-sm text-white placeholder:text-slate-500"
            placeholder="At least 6 characters"
          />
        </Field>

        <div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Country</span>
            <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-white/10 focus-within:border-[#00E5FF] bg-white/5 px-3.5 py-2.5 transition-colors">
              <Globe size={16} className="text-slate-400" />
              <select
                data-testid="signup-country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full bg-transparent outline-none text-sm"
              >
                {countries.map((c) => (
                  <option key={c.name} value={c.name}>{`${c.name} · ${c.currency}`}</option>
                ))}
              </select>
            </div>
          </label>
          <p className="mt-1.5 text-[11px] text-slate-500">
            {currency === "USD"
              ? <>We'll show prices in <span className="font-bold text-[var(--cavilo-blue)]">USD</span>.</>
              : <>We'll show prices in <span className="font-bold text-[var(--cavilo-blue)]">{currency}</span> and USD.</>}
          </p>
        </div>

        <button
          data-testid="signup-submit"
          type="submit"
          disabled={busy}
          className="w-full btn-neon py-3 text-sm disabled:opacity-60 btn-press"
        >
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>

      <p className="text-sm text-slate-500 text-center mt-6">
        Already a member?{" "}
        <Link to="/login" data-testid="signup-to-login" className="font-semibold text-[var(--cavilo-blue)] hover:underline">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}
